const toPositiveInteger = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 0;
  }
  return Math.floor(parsed);
};

const pluralize = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

const defaultTranslate = (_key, fallback, replacements = null) => {
  if (typeof fallback !== 'string' || !replacements || typeof replacements !== 'object') {
    return fallback;
  }
  return fallback.replace(/\{(\w+)\}/g, (_, token) => {
    const replacement = replacements[token];
    return replacement === undefined || replacement === null ? `{${token}}` : String(replacement);
  });
};

export const REWARDED_UNLOCK_CONTEXTS = Object.freeze({
  VOICE_COACH: 'voiceCoach',
  VOICE_NOTE: 'voiceNote',
  MEAL_DAY: 'mealDay',
  QUEST_RESCHEDULE: 'questReschedule',
});

export const formatRewardedAdsCount = (stepsRequired) => {
  const count = toPositiveInteger(stepsRequired);
  if (!count) {
    return 'ads';
  }
  return pluralize(count, 'ad', 'ads');
};

export const formatRewardDuration = (creditSeconds) => {
  const seconds = toPositiveInteger(creditSeconds);
  if (!seconds) {
    return 'voice chat';
  }
  if (seconds % 60 === 0) {
    const minutes = seconds / 60;
    return pluralize(minutes, 'minute', 'minutes');
  }
  return pluralize(seconds, 'second', 'seconds');
};

export const buildVoiceCoachUnlockTitle = (rewardedAds, t = defaultTranslate) => {
  return buildRewardedUnlockCopy(REWARDED_UNLOCK_CONTEXTS.VOICE_COACH, rewardedAds, t).headline;
};

export const buildExtraUseUnlockCta = (rewardedAds, targetLabel, t = defaultTranslate) => {
  switch (targetLabel) {
    case 'voice note':
      return buildRewardedUnlockCopy(REWARDED_UNLOCK_CONTEXTS.VOICE_NOTE, rewardedAds, t).buttonLabel;
    case 'submission':
      return buildRewardedUnlockCopy(REWARDED_UNLOCK_CONTEXTS.MEAL_DAY, rewardedAds, t).buttonLabel;
    case 'reschedule':
      return buildRewardedUnlockCopy(REWARDED_UNLOCK_CONTEXTS.QUEST_RESCHEDULE, rewardedAds, t).buttonLabel;
    default: {
      const stepsRequired = toPositiveInteger(rewardedAds?.stepsRequired);
      if (!stepsRequired) {
        return t('rewardedUnlock.genericExtra', 'Unlock extra {targetLabel}', { targetLabel });
      }
      return t('rewardedUnlock.genericExtra', 'Unlock extra {targetLabel}', { targetLabel });
    }
  }
};

export const buildRewardedUnlockCopy = (context, rewardedAds, t = defaultTranslate) => {
  const stepsRequired = toPositiveInteger(rewardedAds?.stepsRequired);
  const stepsCompleted = Math.min(stepsRequired, toPositiveInteger(rewardedAds?.stepsCompleted));

  const base = {
    fallbackMessage: t('rewardedUnlock.fallbackGeneric', 'Open the Forza iPhone app to unlock this bonus access.'),
    progressLabel: stepsRequired
      ? t('rewardedUnlock.progress', 'Unlock progress: {completed}/{required}', {
        completed: stepsCompleted,
        required: stepsRequired,
      })
      : null,
    upgradeLine: null,
  };

  switch (context) {
    case REWARDED_UNLOCK_CONTEXTS.VOICE_COACH:
      return {
        ...base,
        headline: t('rewardedUnlock.voiceCoach.headline', 'Tap to start your Forza AI Coach'),
        body: t(
          'rewardedUnlock.voiceCoach.body',
          'Get full Voice Coach access with Ultra. Prefer a quick boost? Watch short ads to unlock 1 bonus minute.'
        ),
        buttonLabel: t('rewardedUnlock.voiceCoach.button', 'Unlock 1 bonus minute'),
        fallbackMessage: t('rewardedUnlock.voiceCoach.fallback', 'Open the Forza iPhone app to unlock a bonus minute.'),
        upgradeLine: t('rewardedUnlock.upgrade', 'Upgrade to Ultra'),
      };
    case REWARDED_UNLOCK_CONTEXTS.VOICE_NOTE:
      return {
        ...base,
        headline: t('rewardedUnlock.voiceNote.headline', 'Need one more voice check? Unlock an extra voice note.'),
        buttonLabel: t('rewardedUnlock.voiceNote.button', 'Unlock extra voice note'),
        progressLabel: null,
      };
    case REWARDED_UNLOCK_CONTEXTS.MEAL_DAY:
      return {
        ...base,
        headline: t('rewardedUnlock.mealDay.headline', 'Need one more meal check-in today? Unlock a bonus check-in.'),
        buttonLabel: t('rewardedUnlock.mealDay.button', 'Unlock bonus check-in'),
        progressLabel: null,
      };
    case REWARDED_UNLOCK_CONTEXTS.QUEST_RESCHEDULE:
      return {
        ...base,
        headline: t('rewardedUnlock.questReschedule.headline', 'Want one more plan refresh today? Unlock an extra refresh.'),
        buttonLabel: t('rewardedUnlock.questReschedule.button', 'Unlock extra refresh'),
        progressLabel: null,
      };
    default:
      return {
        ...base,
        headline: t('rewardedUnlock.default.headline', 'Unlock bonus access.'),
        buttonLabel: t('rewardedUnlock.default.button', 'Unlock bonus access'),
      };
  }
};
